import { useEffect, useRef, forwardRef, useImperativeHandle } from 'react'

const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

let scriptLoaded = false
let scriptLoading = false
const loadCallbacks: (() => void)[] = []

function loadTurnstileScript(): Promise<void> {
	if (scriptLoaded) return Promise.resolve()

	return new Promise(resolve => {
		if (scriptLoading) {
			loadCallbacks.push(resolve)
			return
		}

		scriptLoading = true
		const script = document.createElement('script')
		script.src = TURNSTILE_SCRIPT_URL
		script.async = true
		script.defer = true
		script.onload = () => {
			scriptLoaded = true
			scriptLoading = false
			resolve()
			loadCallbacks.forEach(cb => cb())
			loadCallbacks.length = 0
		}
		script.onerror = () => {
			scriptLoading = false
			resolve()
		}
		document.head.appendChild(script)
	})
}

export interface TurnstileRef {
	reset: () => void
	getResponse: () => string | undefined
}

interface TurnstileProps {
	onSuccess?: (token: string) => void
	onError?: () => void
	onExpire?: () => void
	theme?: 'light' | 'dark' | 'auto'
	size?: 'normal' | 'compact' | 'invisible'
	className?: string
}

const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY

/** Max number of automatic retries when the widget errors (e.g. Safari ITP issues) */
const MAX_AUTO_RETRIES = 2

export const Turnstile = forwardRef<TurnstileRef, TurnstileProps>(
	({ onSuccess, onError, onExpire, theme = 'dark', size = 'normal', className }, ref) => {
		const containerRef = useRef<HTMLDivElement>(null)
		const widgetIdRef = useRef<string | null>(null)
		const retryCountRef = useRef(0)
		const callbacksRef = useRef({ onSuccess, onError, onExpire })

		callbacksRef.current = { onSuccess, onError, onExpire }

		useImperativeHandle(ref, () => ({
			reset: () => {
				retryCountRef.current = 0
				if (widgetIdRef.current !== null && window.turnstile) {
					window.turnstile.reset(widgetIdRef.current)
				}
			},
			getResponse: () => {
				if (widgetIdRef.current !== null && window.turnstile) {
					return window.turnstile.getResponse(widgetIdRef.current)
				}
				return undefined
			},
		}))

		useEffect(() => {
			if (!siteKey) return

			let mounted = true

			loadTurnstileScript().then(() => {
				if (!mounted || !containerRef.current || !window.turnstile) return

				widgetIdRef.current = window.turnstile.render(containerRef.current, {
					sitekey: siteKey,
					callback: (token: string) => {
						retryCountRef.current = 0
						callbacksRef.current.onSuccess?.(token)
					},
					'error-callback': () => {
						// Auto-retry on error (helps with Safari ITP/iframe issues)
						if (
							retryCountRef.current < MAX_AUTO_RETRIES &&
							widgetIdRef.current !== null &&
							window.turnstile
						) {
							retryCountRef.current += 1
							window.turnstile.reset(widgetIdRef.current)
							return
						}
						// Exhausted retries — notify the consumer
						callbacksRef.current.onError?.()
					},
					'expired-callback': () => callbacksRef.current.onExpire?.(),
					theme,
					size,
					// Improve Safari compatibility by allowing retry on timeout
					retry: 'auto',
					'retry-interval': 2000,
				})
			})

			return () => {
				mounted = false
				if (widgetIdRef.current !== null && window.turnstile) {
					window.turnstile.remove(widgetIdRef.current)
					widgetIdRef.current = null
				}
			}
		}, [siteKey])

		if (!siteKey) return null

		return <div ref={containerRef} className={className} />
	}
)

Turnstile.displayName = 'Turnstile'
